/**
 * 계측 타입별 판정 기준 상수 (SSOT)
 * @module @ict/shared/measures/constants/judgmentCriteria
 *
 * 이 파일은 Controller(판정 로직)와 Renderer(문서 출력)에서 공통으로 사용하는
 * 판정 기준을 정의합니다. DRY 원칙에 따라 단일 소스로 관리합니다.
 *
 * 사용처:
 * - packages/shared/src/measures/controllers/ (판정 로직)
 * - packages/render-worker/src/docx/sections/measures/ (문서 출력)
 */

// ============================================================================
// 공통 타입 정의
// ============================================================================

/** 판정 기준 테이블 구조 (Controller 판정용) */
export interface CriteriaTable {
  headers: string[];
  rows: string[][];
}

/** 참고문서 형식 판정기준 (렌더링 전용) */
export interface DocCriteria {
  headers: string[];
  rows: string[][];
  footnote?: string;
}

/** 간단한 기준 행 배열 (헤더 없음) */
export type CriteriaRows = string[][];

// ============================================================================
// 1. 전기 카테고리 (ELECTRICAL) - 7개 타입
// ============================================================================

/** AC 입력 전압 판정 기준 */
export const AC_VOLTAGE_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "주의", "불량"],
  rows: [["전압", "정격 ±5%", "정격 ±10%", "정격 ±10% 초과"]],
};

/** AC 전압 판정 임계값 */
export const AC_VOLTAGE_THRESHOLDS = {
  good: 0.05, // ±5%
  caution: 0.1, // ±10%
} as const;

/** DC 입력 전압 판정 기준 */
export const DC_VOLTAGE_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [["전압", "정격 ±2%", "정격 ±2% 초과"]],
};

/** DC 전압 판정 임계값 */
export const DC_VOLTAGE_THRESHOLDS = {
  tolerance: 0.02, // ±2%
} as const;

/** 접지저항 판정 기준 */
export const GROUND_RESISTANCE_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "주의", "불량"],
  rows: [["접지저항", "10Ω 이하", "10~100Ω", "100Ω 초과"]],
};

/** 접지저항 판정 임계값 */
export const GROUND_RESISTANCE_THRESHOLDS = {
  good: 10, // Ω 이하
  caution: 100, // Ω 이하
} as const;

/** 종단저항 판정 기준 */
export const TERMINATION_RESISTANCE_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [["종단저항", "기준값 이상", "기준값 미만"]],
};

/** 종단저항 기본값 (문서: 방송_공동수신설비의 설치기준에 관한 고시) */
export const TERMINATION_RESISTANCE_THRESHOLDS = {
  defaultStandard: 75, // 기본 기준값 75Ω
} as const;

/** 충전전압/전류 판정 기준 */
export const CHARGING_VOLTAGE_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [
    ["충전전압", "13.4~13.6V", "범위 외"],
    ["충전전류", "0.1A 이하", "0.1A 초과"],
  ],
};

/** 충전전압/전류 판정 임계값 */
export const CHARGING_VOLTAGE_THRESHOLDS = {
  voltage: { min: 13.4, max: 13.6 }, // V
  current: { max: 0.1 }, // A
} as const;

/** 배터리 전압 판정 기준 */
export const BATTERY_VOLTAGE_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [["배터리전압", "정격전압 ±2V 이내", "정격전압 ±2V 초과"]],
};

/** 배터리 전압 판정 임계값 */
export const BATTERY_VOLTAGE_THRESHOLDS = {
  tolerance: 2, // ±2V (절대값)
} as const;

/** 전자시계 회선별 전압 판정 기준 */
export const CLOCK_LINE_VOLTAGE_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [
    ["DC12V", "10.8~13.2V (±10%)", "범위 외"],
    ["DC24V", "21.6~26.4V (±10%)", "범위 외"],
    ["AC24V", "21.6~26.4V (±10%)", "범위 외"],
    ["AC220V", "198~242V (±10%)", "범위 외"],
  ],
};

/** 전자시계 회선별 전압 판정 임계값 */
export const CLOCK_LINE_VOLTAGE_THRESHOLDS = {
  DC12V: { min: 10.8, max: 13.2 },
  DC24V: { min: 21.6, max: 26.4 },
  AC24V: { min: 21.6, max: 26.4 },
  AC220V: { min: 198, max: 242 },
} as const;

// ============================================================================
// 2. 통신환경 카테고리 (COMMUNICATION) - 5개 타입
// ============================================================================

/** 링크 동작 속도 판정 기준 */
export const LINK_SPEED_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [["링크속도", "설계속도 80~100%", "설계속도 80% 미만"]],
};

/** 링크 속도 판정 임계값 */
export const LINK_SPEED_THRESHOLDS = {
  min: 0.8, // 80% (최소 허용값)
  max: 1.0, // 100% (최대값)
} as const;

/** 광 송수신 레벨 판정 기준 */
export const OPTICAL_LEVEL_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [
    ["송신기(TX)", "0dBm 이상", "0dBm 미만"],
    ["수신기(RX)", "-8 ~ -3dBm", "범위 외"],
  ],
};

/** 광 레벨 판정 임계값 */
export const OPTICAL_LEVEL_THRESHOLDS = {
  tx: { min: 0 }, // dBm
  rx: { min: -8, max: -3 }, // dBm
} as const;

/** 송신 출력 판정 기준 */
export const TX_OUTPUT_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [["송신출력", "기준값 이하", "기준값 초과"]],
};

/** 출력검사단자 레벨값 판정 기준 */
export const OUTPUT_TERMINAL_LEVEL_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [["출력레벨", "60~80 dBuV", "범위 외"]],
};

/** 출력검사단자 레벨 판정 임계값 */
export const OUTPUT_TERMINAL_LEVEL_THRESHOLDS = {
  min: 60, // dBuV
  max: 80, // dBuV
} as const;

/** 기기 입출력 신호레벨 판정 기준 */
export const IO_SIGNAL_LEVEL_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [
    ["입력레벨", "55~70 dBuV", "범위 외"],
    ["출력레벨", "65~80 dBuV", "범위 외"],
  ],
};

/** I/O 신호 레벨 판정 임계값 */
export const IO_SIGNAL_LEVEL_THRESHOLDS = {
  input: { min: 55, max: 70 }, // dBuV
  output: { min: 65, max: 80 }, // dBuV
} as const;

// ============================================================================
// 3. 전파환경 카테고리 (RADIO WAVE) - 3개 타입
// ============================================================================

/** RSRP 지상파 판정 기준 */
export const RSRP_TERRESTRIAL_CRITERIA: CriteriaTable = {
  headers: ["항목", "양호", "불량"],
  rows: [
    ["SNR", "≥20dB", "<20dB"],
    ["MER", "≥25dB", "<25dB"],
    ["BER", "-", "-"],
    ["RSSI", "-80 ~ -20dBm", "범위 외"],
  ],
};

/** RSRP 지상파 판정 임계값 */
export const RSRP_TERRESTRIAL_THRESHOLDS = {
  snr: { min: 20 }, // dB
  mer: { min: 25 }, // dB
  rssi: { min: -80, max: -20 }, // dBm
} as const;

/** RSRP FM라디오 판정 기준 */
export const RSRP_FM_RADIO_CRITERIA: CriteriaTable = {
  headers: ["항목", "양호(아날로그)", "양호(디지털)", "불량"],
  rows: [
    ["SNR", "≥50dB", "≥20dB", "기준 미만"],
    ["MER", "-", "≥20dB", "<20dB"],
    ["RSSI", "≥-75dBm", "≥-75dBm", "<-75dBm"],
  ],
};

/** RSRP FM라디오 판정 임계값 */
export const RSRP_FM_RADIO_THRESHOLDS = {
  snr: { analog: 50, digital: 20 }, // dB
  mer: { digital: 20 }, // dB (디지털 라디오 전용)
  rssi: { min: -75 }, // dBm
} as const;

/** RSRP 위성방송 판정 기준 */
export const RSRP_SATELLITE_CRITERIA: CriteriaTable = {
  headers: ["항목", "양호(DVB-S)", "양호(DVB-S2)", "불량"],
  rows: [
    ["SNR", ">10dB", "8~12dB", "기준 미만"],
    ["MER", "≥15dB", "≥15dB", "<15dB"],
    ["RSSI", "-65 ~ -25dBm", "-65 ~ -25dBm", "범위 외"],
  ],
};

/** RSRP 위성방송 판정 임계값 */
export const RSRP_SATELLITE_THRESHOLDS = {
  snr: { dvbS: 10, dvbS2: { min: 8, max: 12 } }, // dB
  mer: { min: 15 }, // dB
  rssi: { min: -65, max: -25 }, // dBm
} as const;

// ============================================================================
// 4. 무선신호 카테고리 (WIRELESS) - 2개 타입
// ============================================================================

/** 비상벨 수신감도 판정 기준 */
export const EMERGENCY_BELL_SENSITIVITY_CRITERIA: CriteriaRows = [
  ["수신감도", "≥-100dBm", "<-100dBm"],
];

/** 비상벨 수신감도 판정 임계값 */
export const EMERGENCY_BELL_SENSITIVITY_THRESHOLDS = {
  min: -100, // dBm
} as const;

/** 원격검침 수신감도 판정 기준 */
export const REMOTE_METERING_SENSITIVITY_CRITERIA: CriteriaRows = [
  ["424-447MHz", "-110 ~ -100dBm", "범위 외"],
  ["920-923MHz", "-137 ~ -110dBm", "범위 외"],
];

// 원격검침 임계값은 frequencyThresholds.ts의 REMOTE_METERING_THRESHOLDS 사용

// ============================================================================
// 5. 전계강도 카테고리 (FIELD STRENGTH) - 2개 타입
// ============================================================================

/** 동축케이블 전계강도 판정 기준 */
export const COAXIAL_FIELD_STRENGTH_CRITERIA: CriteriaRows = [
  ["54-216MHz", "≤20μV/m", ">20μV/m"],
  ["216-400MHz", "≤15μV/m", ">15μV/m"],
  ["400MHz 이상", "≤10μV/m", ">10μV/m"],
];

// 동축케이블 임계값은 frequencyThresholds.ts의 COAXIAL_FIELD_STRENGTH_THRESHOLDS 사용

/** 무선신호 전계강도 판정 기준 */
export const WIRELESS_FIELD_STRENGTH_CRITERIA: CriteriaRows = [
  ["전계강도", "≥15μV/m (통신가능)", "<15μV/m"],
];

/** 무선신호 전계강도 판정 임계값 */
export const WIRELESS_FIELD_STRENGTH_THRESHOLDS = {
  min: 15, // μV/m (통신 가능 최소값)
  good: 50, // μV/m (양호)
  excellent: 150, // μV/m (매우 양호)
} as const;

// ============================================================================
// 6. RF카드 카테고리 (RF_CARD) - 2개 타입
// ============================================================================

/** RF카드 자기장 세기 판정 기준 */
export const RF_MAGNETIC_FIELD_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [["자기장 세기", "1.5~7.5 A/m", "범위 외"]],
};

/** RF카드 자기장 세기 판정 임계값 */
export const RF_MAGNETIC_FIELD_THRESHOLDS = {
  min: 1.5, // A/m
  max: 7.5, // A/m
} as const;

/** RF카드 인식거리 판정 기준 */
export const RF_RECOGNITION_DISTANCE_CRITERIA: CriteriaTable = {
  headers: ["주파수 대역", "양호", "불량"],
  rows: [
    ["125-134kHz", "≤10cm", ">10cm"],
    ["13.56MHz(근접)", "≤10cm", ">10cm"],
    ["13.56MHz(근거리)", "≤1.5m", ">1.5m"],
    ["860-960MHz", "≤12m", ">12m"],
  ],
};

// RF 인식거리 임계값은 frequencyThresholds.ts의 RF_RECOGNITION_DISTANCE_THRESHOLDS 사용

// ============================================================================
// 7. 기타 카테고리 (MISC) - 3개 타입
// ============================================================================

/** 온습도 판정 기준 */
export const TEMPERATURE_HUMIDITY_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [
    ["온도", "16~27℃", "16℃ 미만 또는 27℃ 초과"],
    ["습도", "30~70%", "30% 미만 또는 70% 초과"],
  ],
};

/** 온습도 판정 임계값 */
export const TEMPERATURE_HUMIDITY_THRESHOLDS = {
  temperature: { min: 16, max: 27 }, // ℃
  humidity: { min: 30, max: 70 }, // %
} as const;

/** 축전지 비중 판정 기준 (문서: 23. 축전지 비중 측정 기록표.docx) */
export const BATTERY_SPECIFIC_GRAVITY_CRITERIA: CriteriaTable = {
  headers: ["점검항목", "비중(S.G)", "판정"],
  rows: [
    ["완전충전", "1.260 ~ 1.280", "양호"],
    ["정상운전", "1.240 ~ 1.260", "양호"],
    ["충전부족", "1.220 ~ 1.240", "주의"],
    ["방전상태", "1.200 이하", "불량"],
  ],
};

/** 축전지 비중 판정 임계값 (문서 기준 4단계) */
export const BATTERY_SPECIFIC_GRAVITY_THRESHOLDS = {
  fullyCharged: { min: 1.26, max: 1.28 }, // 완전충전
  normalOperation: { min: 1.24, max: 1.26 }, // 정상운전
  underCharged: { min: 1.22, max: 1.24 }, // 충전부족
  discharged: { max: 1.2 }, // 방전상태 (1.200 이하)
} as const;

/** 맨홀 유해가스 판정 기준 */
export const MANHOLE_TOXIC_GAS_CRITERIA: CriteriaTable = {
  headers: ["구분", "양호", "불량"],
  rows: [
    ["산소(O₂)", "18~23.5%", "18% 미만 또는 23.5% 초과"],
    ["황화수소(H₂S)", "<10ppm", "10ppm 이상"],
    ["일산화탄소(CO)", "<30ppm", "30ppm 이상"],
  ],
};

/** 맨홀 유해가스 판정 임계값 */
export const MANHOLE_TOXIC_GAS_THRESHOLDS = {
  o2: { min: 18, max: 23.5 }, // %
  h2s: { max: 10 }, // ppm
  co: { max: 30 }, // ppm
} as const;

// ============================================================================
// 참고문서 형식 판정기준 (DocCriteria) - 렌더링 전용
// ============================================================================

// --- 전기 카테고리 (7개) ---

/** AC 입력 전압 - 참고문서 형식 판정기준 */
export const AC_VOLTAGE_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "기준", "비 고"],
  rows: [["통신 관련 설비", "220V ±10%", ""]],
  footnote: "* 전기사업법 시행규칙 [별표3] 표준전압 및 허용오차 기준 ±10% 이내",
};

/** DC 입력 전압 - 참고문서 형식 판정기준 */
export const DC_VOLTAGE_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "기준", "비 고"],
  rows: [["통신 관련 설비", "정격전압 ±2%", ""]],
  footnote: "* 제조사별 사양에 따라 상이함.",
};

/** 접지저항 - 참고문서 형식 판정기준 */
export const GROUND_RESISTANCE_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "기준", "비 고"],
  rows: [["통신관련 시설", "10[Ω]이하", "100[Ω]이하\n(기술기준 제5조 2항 1호~7호)"]],
  footnote: "* 접지설비·구내통신설비·선로설비 및 통신공동구 등에 대한 기술기준",
};

/** 종단 저항 - 참고문서 형식 판정기준 (4열) */
export const TERMINATION_RESISTANCE_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "사용조건", "기준 판정", "비 고"],
  rows: [
    ["유휴 분배단자와\n유휴 분기단자", "임피던스의 변화없이\n분배하거나 분기", "75[Ω] 이상", ""],
  ],
  footnote: "* 방송_공동수신설비의 설치기준에 관한 고시",
};

/** 충전전압/전류 - 참고문서 형식 판정기준 */
export const CHARGING_VOLTAGE_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "기준", "비 고"],
  rows: [
    [
      "무정전전원장치(UPS)",
      "충전전압 : 셀당 2.23~2.27V\n(12V형의 경우 13.4~13.6V)\n충전전류 : 초기 충전시 최대값 확인 후\n완충 시 0.1A이하 수렴",
      "",
    ],
  ],
  footnote: "* 한국산업표준(KSC IEC 62040-3) 및 제조사별 사양에 따라 상이함.",
};

/** 배터리 전압 - 참고문서 형식 판정기준 */
export const BATTERY_VOLTAGE_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "기준", "비 고"],
  rows: [["무정전전원장치(UPS)", "배터리전압±2 이내", ""]],
  footnote: "* 제조사별 사양에 따라 상이함.",
};

/** 전자시계 회선별 전압 - 참고문서 형식 판정기준 (4열, 다중 행) */
export const CLOCK_LINE_VOLTAGE_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "송출방식", "기준값(도착전압정상범위)", "비 고"],
  rows: [
    ["전기시계 시스템", "DC 12V", "10.8 ~13.2V", ""],
    ["", "DC 24V", "21.6 ~26.4V", ""],
    ["", "AC 24V", "21.6 ~26.4V", ""],
    ["", "AC 220V", "198 ~ 242V", ""],
  ],
  footnote: "* KS C IEC, 전원설비 허용오차±10",
};

// --- 통신환경 카테고리 (5개) ---

/** 링크 동작 속도 - 참고문서 형식 판정기준 */
export const LINK_SPEED_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "점검항목", "기준", "비 고"],
  rows: [
    ["네트워크 설비", "100Mbps 인터넷", "정상속도 80~100Mbps", ""],
    ["", "500Mbps 인터넷", "정상속도 400~500Mbps", ""],
    ["", "1Gbps 인터넷", "정상속도 800~1Gbps", ""],
  ],
  footnote: "* 정보통신공사업법, 건축물 정보통신설비 설치기준, IEEE 802.3 이더넷 표준",
};

/** 광 송수신 레벨 - 참고문서 형식 판정기준 */
export const OPTICAL_LEVEL_DOC_CRITERIA: DocCriteria = {
  headers: ["구 분", "단 위", "기준값", "비 고"],
  rows: [
    ["광 입출력", "[dBm]", "광 출력 0 이상", "광 송신기"],
    ["", "", "광 입력 -8 ~ -3", "광 수신기"],
  ],
  footnote: "* 방송_공동수신설비의 설치기준",
};

/** 송신 출력 - 참고문서 형식 판정기준 */
export const TX_OUTPUT_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "점검항목", "기준", "비 고"],
  rows: [["원격검침 시스템", "단말기 송신 출력", "10 mW 이하", ""]],
  footnote: "* 전파법 소출력 무선기기 기준",
};

/** 출력검사단자 레벨값 - 참고문서 형식 판정기준 */
export const OUTPUT_TERMINAL_LEVEL_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "기준", "비 고"],
  rows: [["종합유선방송\n구내전송선로 설비", "60 ~ 80 dbuV", ""]],
  footnote: "* 방송통신설비 기술기준에 따른 수신기 허용 입력범위",
};

/** 기기 입출력 신호레벨 - 참고문서 형식 판정기준 */
export const IO_SIGNAL_LEVEL_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "점검항목", "기준", "비 고"],
  rows: [
    ["종합유선방송\n구내전송선로 설비", "입력레벨", "55~70 dbuV", ""],
    ["", "출력레벨", "65~80 dbuV", ""],
  ],
  footnote: "* 기기 입·출력 신호레벨은 방송통신설비 기술기준 및 CATV 수신기 허용 입력범위",
};

// --- 전파환경 카테고리 (3개) ---

/** RSRP 지상파 - 참고문서 형식 판정기준 */
export const RSRP_TERRESTRIAL_DOC_CRITERIA: DocCriteria = {
  headers: ["측정항목", "판정기준", "비 고"],
  rows: [
    ["SNR(신호대잡음비)", "20dB 이상", ""],
    ["MER(변조오류율)", "25dB 이상", ""],
    ["BER(비트오류율)", "이하", ""],
    ["RSSI(수신신호강도 표시)", "-80dBm ~ -20dBm\n(또는 25dBuV ~ 80dBuV)", ""],
  ],
  footnote: "* 방송통신위원회 고시 및 ATSC 디지털 텔레비전 전송 측정 및 준수에 관한 권장실무 지침",
};

/** RSRP FM라디오 - 참고문서 형식 판정기준 */
export const RSRP_FM_RADIO_DOC_CRITERIA: DocCriteria = {
  headers: ["측정항목", "판정기준", "비 고"],
  rows: [
    ["SNR(신호대잡음비)", "50dB 이상", "아날로그FM"],
    ["MER(변조오류율)", "20dB 이상", "디지털 라디오"],
    ["BER(비트오류율)", "~", "디지털 라디오"],
    ["RSSI(수신신호강도 표시)", "-75dBm 이상", "아날로그FM"],
  ],
  footnote: "* 장비 제조사의 사양에 따라 상이함.",
};

/** RSRP 위성방송 - 참고문서 형식 판정기준 */
export const RSRP_SATELLITE_DOC_CRITERIA: DocCriteria = {
  headers: ["측정항목", "판정기준", "비 고"],
  rows: [
    ["SNR(신호대잡음비)", "DVB-S 기준 : >10dB\nDVB-S2 기준 : >8dB ~ 12dB", ""],
    ["MER(변조오류율)", "15dB 이상", "20dB 이상 (권장)"],
    ["BER(비트오류율)", "", ""],
    ["RSSI(수신신호강도 표시)", "-65dBm ~ -25dBm", ""],
  ],
  footnote: "* 장비 제조사의 사양에 따라 상이함.",
};

// --- 무선신호 카테고리 (2개) ---

/** 비상벨 수신감도 - 참고문서 형식 판정기준 */
export const EMERGENCY_BELL_SENSITIVITY_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "기준", "비 고"],
  rows: [["비상벨 설비", "-100dBm 이하", ""]],
  footnote: "* 전파법, 무선설비기술기준에 의한 기준",
};

/** 원격검침 수신감도 - 참고문서 형식 판정기준 */
export const REMOTE_METERING_SENSITIVITY_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "주파수대역", "기준", "비 고"],
  rows: [
    ["원격검침 시스템", "424 ~ 447 MHz", "-100 ~ 110dBm", ""],
    ["", "920 ~ 923 MHz", "-110 ~ 137dBm", ""],
  ],
  footnote: "* 무선설비 성능규격",
};

// --- 전계강도 카테고리 (2개) ---

/** 동축케이블 전계강도 - 참고문서 형식 판정기준 */
export const COAXIAL_FIELD_STRENGTH_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "주파수 대역", "기준", "비 고"],
  rows: [
    ["원격검침 시스템", "54 ~ 216MHz", "20 μV/m", ""],
    ["", "216MHz ~ 400MHz", "15 μV/m", ""],
    ["", "400MHz 이상", "10 μV/m", ""],
  ],
  footnote: "* 방송통신설비 기술 기준에 관한 규정",
};

/** 무선신호 전계강도 - 참고문서 형식 판정기준 */
export const WIRELESS_FIELD_STRENGTH_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "수신감도(dBm)", "전계강도(μV/m)", "비 고(통신가능판단)"],
  rows: [
    ["원격검침 시스템", "-90 dBm", "150μV/m", "매우 양호"],
    ["", "-100 dBm", "50μV/m", "양호"],
    ["", "-110 dBm", "15μV/m", "최소 수신"],
  ],
  footnote:
    "* 전계강도 기준은 전파법 및 무선설비 기술기준에 따른 무선 수신기 최소 수신감도를 현장 측정 가능 지표(μV/m)로 환산한 값임.",
};

// --- RF카드 카테고리 (2개) ---

/** RF카드 자기장 세기 - 참고문서 형식 판정기준 */
export const RF_MAGNETIC_FIELD_DOC_CRITERIA: DocCriteria = {
  headers: ["측정항목", "측정 단위", "기준", "비 고"],
  rows: [["자기장 세기", "[A/m]", "1.5 ~ 7.5 [A/m]", ""]],
  footnote: "* 비접촉식 스마트 카드(Proximity Cards)의 국제 표준(ISO/IEC 14443)",
};

/** RF카드 인식거리 - 참고문서 형식 판정기준 */
export const RF_RECOGNITION_DISTANCE_DOC_CRITERIA: DocCriteria = {
  headers: ["측정항목", "주파수", "표준 인식거리", "비 고"],
  rows: [
    ["인식거리", "125~134[kHz]", "10cm 이내", "ISO 11784/11785"],
    ["", "13.56[MHz]", "10cm 이내", "ISO 14443(근접식)"],
    ["", "13.56[MHz]", "최대 1m ~ 1.5m", "ISO 15693(근거리)"],
    ["", "860~960[MHz]", "수m ~ 12m 이상", "ISO 18000-6C"],
  ],
  footnote: "* ISO 국제 표준 규격 기준",
};

// --- 기타 카테고리 (3개) ---

/** 온습도 - 참고문서 형식 판정기준 */
export const TEMPERATURE_HUMIDITY_DOC_CRITERIA: DocCriteria = {
  headers: ["보호시설", "온도 기준", "습도 기준", "비 고"],
  rows: [["전산실 및 통신센터의\n환경조건", "16[℃] ~ 27[℃]", "30[%] ~ 70[%]", ""]],
  footnote: "* (구)전산망 기술기준에 관한 규칙",
};

/** 축전지 비중 - 참고문서 형식 판정기준 */
export const BATTERY_SPECIFIC_GRAVITY_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "점검항목", "비중(S.G)", "비 고"],
  rows: [
    ["무정전전원장치(UPS)", "완전충전", "1.260 ~ 1.280", "※25℃ 기준"],
    ["", "정상운전", "1.240 ~ 1.260", ""],
    ["", "충전부족", "1.220 ~ 1.240", ""],
    ["", "방전상태", "1.200 이하", ""],
  ],
  footnote: "* 축전지 비중(S.G)는 KS C 8542, IEEE450 / 충전상태는 제조사 및 KS기준",
};

/** 맨홀 유해가스 - 참고문서 형식 판정기준 */
export const MANHOLE_TOXIC_GAS_DOC_CRITERIA: DocCriteria = {
  headers: ["사용설비", "측정항목", "기준값", "비 고"],
  rows: [
    ["국선인입 설비", "산소(O₂)", "18% ~ 23.5% 이하", "18%미만 산소결핍 작업금지"],
    ["", "황화수소(H₂S)", "10 ppm 미만", ""],
    ["", "일산화탄소(CO)", "30 ppm 미만", ""],
  ],
  footnote: "* 산업안전보건법 밀폐공간 작업 시 조치 (규칙 제 618조 제1호 관련)",
};

// ============================================================================
// 헬퍼 함수
// ============================================================================

/**
 * CriteriaRows를 CriteriaTable로 변환
 * @param rows - 기준 행 배열
 * @param headers - 테이블 헤더 (기본값: ["구분", "양호", "불량"])
 */
export function rowsToCriteriaTable(
  rows: CriteriaRows,
  headers: string[] = ["구분", "양호", "불량"]
): CriteriaTable {
  return { headers, rows };
}
